/**
 * Badge Modal Service
 * Manages global badge earned modal state with queue support for displaying multiple badges.
 */

import { Badge } from '../types/index';

// State
let isOpen = false;
let currentBadge: Badge | null = null;
const badgeQueue: Badge[] = [];

// Test mode for injecting test badge
let testBadgeMode = false;

// Track which badge IDs have been shown in modals (in-memory cache)
// This is used as a fallback, but primary check is badge.metadata.modalShown
const shownBadgeIds: Set<string> = new Set();

// Listeners
type Listener = (state: { isOpen: boolean; currentBadge: Badge | null }) => void;
const listeners: Set<Listener> = new Set();

function notifyListeners() {
  listeners.forEach(listener => listener({ isOpen, currentBadge }));
}

// Show a badge modal (adds to queue if modal is already open)
// If profileId is provided, will update badge metadata to mark modal as shown
export function showBadge(badge: Badge, profileId?: string) {
  // Mark badge as shown in memory cache
  shownBadgeIds.add(badge.id);
  
  // Update badge metadata on backend if profileId is provided
  if (profileId && badge.metadata?.modalShown !== true) {
    // Import progressService dynamically to avoid circular dependencies
    import('./progressService').then(({ progressService }) => {
      progressService.markBadgeModalShown(profileId, badge.id).catch(err => {
        // Silent failure - don't interrupt user experience
        console.error('Failed to mark badge modal as shown:', err);
      });
    });
  }
  
  if (isOpen && currentBadge) {
    // Modal is already showing, add to queue
    badgeQueue.push(badge);
  } else {
    // Modal is closed, show immediately
    currentBadge = badge;
    isOpen = true;
    notifyListeners();
  }
}

// Close the current badge modal and show next in queue if available
export function closeBadge() {
  if (badgeQueue.length > 0) {
    // Show next badge in queue
    currentBadge = badgeQueue.shift() || null;
    isOpen = true;
  } else {
    // No more badges, close modal
    isOpen = false;
    currentBadge = null;
  }
  notifyListeners();
}

// Subscribe to state changes
export function subscribeToBadgeModal(listener: Listener): () => void {
  listeners.add(listener);
  // Immediately notify with current state
  listener({ isOpen, currentBadge });
  return () => listeners.delete(listener);
}

// Test mode setter
export function setTestBadgeMode(enabled: boolean) {
  testBadgeMode = enabled;
}

// Get test badge mode
export function getTestBadgeMode(): boolean {
  return testBadgeMode;
}

// Get test badge (for use in badge detection)
export function getTestBadge(): Badge {
  return {
    id: 'test-badge-1',
    name: 'Test Badge',
    description: 'This is a test badge for testing the badge earned modal!',
    imageUrl: 'https://storage.googleapis.com/rev-data-dev-mvp/badge/c686a753-4d40-45de-ba1e-0f30551527ec/public/image/c686a753-4d40-45de-ba1e-0f30551527ec.svg',
  };
}

// Check if a badge has already been shown (either in metadata or in-memory cache)
export function hasBadgeBeenShown(badge: Badge): boolean {
  const modalShownInMetadata = badge.metadata?.modalShown === true;
  const modalShownInMemory = shownBadgeIds.has(badge.id);
  return modalShownInMetadata || modalShownInMemory;
}

// Check badges array and show modals for any that haven't been shown yet
// Checks badge.metadata.modalShown to determine if modal has been shown
export function checkAndShowNewBadges(badges: Badge[], profileId?: string): void {
  if (!badges || badges.length === 0) {
    return;
  }

  // Filter out badges that have already been shown (check metadata first, then in-memory cache)
  const newBadges = badges.filter(badge => !hasBadgeBeenShown(badge));

  // Show modals for each new badge and mark as shown
  newBadges.forEach(badge => {
    showBadge(badge, profileId);
  });
}

// Convenience export
export const badgeModalService = {
  show: showBadge,
  close: closeBadge,
  subscribe: subscribeToBadgeModal,
  setTestMode: setTestBadgeMode,
  getTestMode: getTestBadgeMode,
  getTestBadge: getTestBadge,
  checkAndShowNewBadges: checkAndShowNewBadges,
  hasBadgeBeenShown: hasBadgeBeenShown,
};
